import { create } from "zustand";
import {
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  onIdTokenChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  type User,
} from "firebase/auth";
import { firebaseAuth } from "../lib/firebase";

/**
 * A signed-in identity that is NOT backed by Firebase. The /dev mount uses it so
 * every authenticated screen is reachable with no backend and no credentials.
 * It never yields a token, so it cannot be used against the live API.
 */
export interface DevSession {
  email: string;
  name: string;
  uid: string;
  role: string;
  superAdmin: boolean;
}

export const DEV_SESSION: DevSession = {
  email: "dana@acme.ai",
  name: "Dana Kessler",
  uid: "uid_dk01",
  role: "owner",
  superAdmin: true,
};

interface AuthState {
  user: User | null;
  claims: Record<string, unknown> | null;
  isSuperAdmin: boolean;
  loading: boolean;
  initialized: boolean;
  /** Set only under /dev; short-circuits Firebase entirely. */
  devSession: DevSession | null;
  init: () => void;
  /** Detaches the Firebase listener and the refresh interval. */
  dispose: () => void;
  signInEmail: (email: string, password: string) => Promise<void>;
  signUpEmail: (email: string, password: string) => Promise<void>;
  signInGoogle: () => Promise<void>;
  signOutUser: () => Promise<void>;
  getToken: (forceRefresh?: boolean) => Promise<string | null>;
  enterDevMode: () => void;
  exitDevMode: () => void;
}

let refreshTimer: ReturnType<typeof setInterval> | null = null;
let unsubscribeAuth: (() => void) | null = null;
// Bumped by dispose(); an in-flight getIdTokenResult from a torn-down listener
// captures the old value and drops its result.
let installation = 0;

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  claims: null,
  isSuperAdmin: false,
  devSession: null,
  loading: true,
  initialized: false,

  enterDevMode: () => {
    set({ devSession: DEV_SESSION, isSuperAdmin: DEV_SESSION.superAdmin, loading: false });
  },
  exitDevMode: () => {
    set({ devSession: null, isSuperAdmin: get().claims?.superAdmin === true });
  },

  dispose: () => {
    installation += 1;
    unsubscribeAuth?.();
    unsubscribeAuth = null;
    if (refreshTimer) {
      clearInterval(refreshTimer);
      refreshTimer = null;
    }
    set({ initialized: false });
  },

  init: () => {
    if (get().initialized) return;
    set({ initialized: true });
    const auth = firebaseAuth();
    const install = installation;
    // A generation counter sequences the async callbacks: a slow getIdTokenResult
    // from an older event must never overwrite state set by a newer one (e.g. a
    // sign-out that raced ahead of an in-flight sign-in callback).
    let generation = 0;
    // onIdTokenChanged (not onAuthStateChanged) also fires on token refresh, so
    // custom-claim changes (superAdmin grant/revoke) propagate without a full
    // sign-in/out cycle.
    unsubscribeAuth = onIdTokenChanged(auth, async (u) => {
      const gen = ++generation;
      if (install !== installation) return;
      if (!u) {
        set({ user: null, claims: null, isSuperAdmin: false, loading: false });
        return;
      }
      try {
        const res = await u.getIdTokenResult();
        if (gen !== generation || install !== installation) return; // superseded
        set({
          user: u,
          claims: res.claims as Record<string, unknown>,
          isSuperAdmin: res.claims?.superAdmin === true,
          loading: false,
        });
      } catch {
        if (gen !== generation || install !== installation) return;
        // Couldn't read claims — keep the user but clear claims rather than
        // hang on loading=true forever.
        set({ user: u, claims: null, isSuperAdmin: false, loading: false });
      }
    });
    // Proactively refresh the ID token every 30 minutes while the tab is open;
    // the resulting onIdTokenChanged updates claims, and the http client also
    // force-refreshes reactively on a 401.
    if (!refreshTimer) {
      refreshTimer = setInterval(
        () => {
          const u = firebaseAuth().currentUser;
          if (u) u.getIdToken(true).catch(() => {});
        },
        30 * 60 * 1000,
      );
    }
  },

  signInEmail: async (email, password) => {
    await signInWithEmailAndPassword(firebaseAuth(), email, password);
  },
  signUpEmail: async (email, password) => {
    await createUserWithEmailAndPassword(firebaseAuth(), email, password);
  },
  signInGoogle: async () => {
    await signInWithPopup(firebaseAuth(), new GoogleAuthProvider());
  },
  signOutUser: async () => {
    if (get().devSession) {
      set({ devSession: null });
      return;
    }
    await signOut(firebaseAuth());
  },

  getToken: async (forceRefresh = false) => {
    // A dev session is deliberately tokenless: it must never authenticate a
    // request against a real backend.
    if (get().devSession) return null;
    const u = firebaseAuth().currentUser;
    if (!u) return null;
    return u.getIdToken(forceRefresh);
  },
}));

/** True when someone is signed in, by either route. */
export function useIsAuthed(): boolean {
  return useAuthStore((s) => s.user !== null || s.devSession !== null);
}

/**
 * The identity the shell renders in the sidebar footer. Each field is selected
 * separately so the selector returns a primitive — returning a fresh object
 * would re-render on every store write under zustand's Object.is comparison.
 */
export function useIdentity(): { email: string; role: string; initials: string } {
  const email = useAuthStore((s) => s.devSession?.email ?? s.user?.email ?? "unknown");
  const displayName = useAuthStore((s) => s.devSession?.name ?? s.user?.displayName ?? null);
  const devRole = useAuthStore((s) => s.devSession?.role ?? null);
  const isSuperAdmin = useAuthStore((s) => s.isSuperAdmin);
  const role = devRole ?? (isSuperAdmin ? "superadmin" : "member");
  return { email, role, initials: initialsFor(displayName, email) };
}

/** "Dana Kessler" -> "DK"; falls back to the first two letters of the local part. */
function initialsFor(displayName: string | null, email: string): string {
  const parts = (displayName ?? "").trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return ((email.split("@")[0] ?? "").slice(0, 2) || "??").toUpperCase();
}
